/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React from 'react';
import LegalPageLayout from './LegalPageLayout';
import { LEGAL_OWNER } from '@/lib/legalOwner';

const DPAPage = () => (
  <LegalPageLayout
    activeKey="dpa"
    title="Data Processing Agreement (DPA)"
    subtitle="Modello di accordo tra il Titolare del Trattamento (clinica) e i responsabili esterni."
    lastUpdated={LEGAL_OWNER.lastReview}
  >
    <div className="rounded-lg border-l-4 border-primary bg-primary/10 p-3 not-prose mb-6">
      <p className="text-sm">
        <strong>Nota preliminare.</strong> Il Titolare del software SanitàNexus — {LEGAL_OWNER.fullName}
        {' '}(CF {LEGAL_OWNER.fiscalCode}) — <strong>non è né Titolare né Responsabile del Trattamento</strong>
        {' '}dei dati sanitari processati all'interno di un'installazione cliente. Il presente documento
        è un <em>modello</em> che la clinica acquirente può adottare per regolare i propri rapporti con
        eventuali fornitori di servizi accessori (hosting, backup, corriere, laboratori esterni).
      </p>
    </div>

    <h2>1. Parti</h2>
    <p>
      Il presente Data Processing Agreement (DPA) regola i rapporti tra:
    </p>
    <ul>
      <li><strong>Titolare del Trattamento</strong>: la struttura sanitaria che installa SanitàNexus sulla
        propria infrastruttura;</li>
      <li><strong>Responsabile del Trattamento</strong>: il soggetto esterno che, per conto della struttura,
        effettua operazioni sui dati personali dei pazienti (es. provider di hosting privato, servizio di
        backup, corriere referti).</li>
    </ul>

    <h2>2. Ruolo del fornitore del software (SanitàNexus)</h2>
    <p>
      Marco Panzetti, quale licenziante del software, fornisce esclusivamente il codice sorgente ed
      eventuali aggiornamenti; <strong>non processa dati personali</strong> in nome e per conto del
      Titolare e non è pertanto un Responsabile del Trattamento ai sensi dell'Art. 28 GDPR.
    </p>

    <h2>3. Oggetto e durata</h2>
    <p>
      Il Responsabile tratta i dati personali comunicati dal Titolare per la sola durata del contratto
      di servizio sottostante, entro i termini definiti dallo stesso.
    </p>

    <h2>4. Natura e finalità del trattamento</h2>
    <p>
      Il Responsabile può accedere ai dati esclusivamente per gli scopi tecnici del servizio commissionato
      (hosting, backup, invio referti, integrazione FSE). Ogni ulteriore trattamento è vietato salvo
      diversa istruzione scritta del Titolare.
    </p>

    <h2>5. Categorie di dati e interessati</h2>
    <ul>
      <li>Dati identificativi dei pazienti (nome, cognome, data di nascita, codice fiscale).</li>
      <li>Dati sanitari (categorie particolari ex Art. 9 GDPR): diagnosi, prescrizioni, referti.</li>
      <li>Dati amministrativi: contatti, appuntamenti, fatturazione.</li>
    </ul>

    <h2>6. Obblighi del Responsabile</h2>
    <ul>
      <li>Trattare i dati solo su istruzione documentata del Titolare;</li>
      <li>Garantire riservatezza tramite obblighi contrattuali con i propri incaricati;</li>
      <li>Adottare le misure di sicurezza di cui all'Art. 32 GDPR;</li>
      <li>Assistere il Titolare nell'evasione delle richieste degli interessati;</li>
      <li>Notificare al Titolare qualsiasi Data Breach entro 24 ore dalla scoperta;</li>
      <li>Al termine del contratto, cancellare o restituire integralmente i dati.</li>
    </ul>

    <h2>7. Sub-responsabili</h2>
    <p>
      Il ricorso a sub-responsabili è consentito previa autorizzazione scritta del Titolare e con
      analoghe garanzie contrattuali (back-to-back).
    </p>

    <h2>8. Audit</h2>
    <p>
      Il Titolare ha diritto di svolgere audit sull'operato del Responsabile con preavviso di 30 giorni.
    </p>

    <h2>9. Legge applicabile e foro</h2>
    <p>
      Il presente DPA è regolato dalla legge italiana. Per ogni controversia è competente in via
      esclusiva il Foro di Pavia (PV).
    </p>

    <h2>10. Contatto per il modello</h2>
    <p>
      Il presente modello è fornito a titolo esemplificativo. Le cliniche sono invitate a farlo
      validare dal proprio consulente legale. Segnalazioni di errori/miglioramenti:
      <a href={`mailto:${LEGAL_OWNER.contactEmail}`}> {LEGAL_OWNER.contactEmail}</a>.
    </p>
  </LegalPageLayout>
);

export default DPAPage;
